// Keyword-to-Sales Attribution — the PRD's five sub-features.
//
// The Keyword Performance Dashboard says how each term performs. This says
// what search is worth. They read the same stored reports, so a term's
// purchases are the same number on both, and neither recomputes the other's
// figures.
//
// Two of the five sub-features need the Amazon Ads API, which is not
// integrated. They appear, locked, saying what is missing — never as a zero,
// because a zero would be a claim about the seller's advertising.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowLeft, FiLink, FiLock } from 'react-icons/fi';

import shell from '../SellerIntelligence.module.css';
import s from './SearchAttribution.module.css';
import AuthContext from '../../../context/AuthContext';
import { useAmazonStatus } from '../../../hooks/useAmazonStatus';
import FeatureCards from './components/FeatureCards';
import ProductBlocks from './components/ProductBlocks';
import AccountView from './components/AccountView';
import LongTail from './components/LongTail';
import TermRevenue from './components/TermRevenue';
import WhatChanged from '../shared/WhatChanged';
import { NotConnectedError, getAccount, getChanges, getLongTail, getOverview, getProducts,
         getTerm } from './api';

// SF2 and SF3 share a tab: they are the same blocked integration, and giving
// each an empty screen would say twice what one says once.
const TABS = [
  { id: 'products', label: 'Products', ref: 'SF1 · SF4' },
  { id: 'account', label: 'Account', ref: 'SF1 · SF4' },
  { id: 'long_tail', label: 'Long tail', ref: 'SF5' },
  // A product leaning harder on one term, or losing search share, is about a
  // product — which is this feature's unit. What changed about terms belongs
  // to Keyword Performance and is shown there.
  { id: 'changed', label: 'What changed', ref: 'SF1 · SF4' },
  { id: 'roi', label: 'Organic vs paid', ref: 'SF2 · SF3' },
];

export default function SearchAttribution() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const { connected, loading: checking } = useAmazonStatus(token);

  const [tab, setTab] = useState('products');
  const [period, setPeriod] = useState('WEEK');
  const [startDate, setStartDate] = useState('');
  const [windowOptions, setWindowOptions] = useState([]);

  const [overview, setOverview] = useState(null);
  const [products, setProducts] = useState(null);
  const [account, setAccount] = useState(null);
  const [tail, setTail] = useState(null);
  const [changes, setChanges] = useState(null);
  // Arrived from Keyword Performance with a term in hand. Its own state and
  // its own fetch, so opening one term does not disturb the product list
  // underneath it.
  const [searchParams, setSearchParams] = useSearchParams();
  const termParam = searchParams.get('term') || '';
  const [term, setTerm] = useState(null);
  const [termLoading, setTermLoading] = useState(false);

  const [locked, setLocked] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notConnected, setNotConnected] = useState(false);

  const fail = useCallback((e) => {
    if (e instanceof NotConnectedError) setNotConnected(true);
    else setError(e.message);
  }, []);

  useEffect(() => {
    if (!token || !connected) return undefined;
    let live = true;
    getOverview(token, { period, start_date: startDate })
      .then((r) => {
        if (!live) return;
        setOverview(r);
        setWindowOptions(r.window_options || []);
        setLocked(r.locked ? r : null);
        if (r.start_date && !startDate) setStartDate(r.start_date);
      })
      .catch((e) => { if (live) fail(e); });
    return () => { live = false; };
  }, [token, connected, period, startDate, fail]);

  const load = useCallback(async () => {
    if (!token || !connected || tab === 'roi') return;
    setLoading(true);
    setError(null);
    try {
      const args = { period, start_date: startDate };
      if (tab === 'products') setProducts(await getProducts(token, args));
      else if (tab === 'account') setAccount(await getAccount(token, args));
      else if (tab === 'long_tail') setTail(await getLongTail(token, args));
      else if (tab === 'changed') setChanges(await getChanges(token, args));
    } catch (e) { fail(e); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, connected, tab, period, startDate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!token || !connected || !termParam) { setTerm(null); return; }
    setTermLoading(true);
    getTerm(token, termParam, { period, start_date: startDate })
      .then(setTerm)
      .catch((e) => fail(e))
      .finally(() => setTermLoading(false));
  }, [token, connected, termParam, period, startDate, fail]);

  const clearTerm = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('term');
    setSearchParams(next, { replace: true });
  };

  const adsCards = (overview?.cards || []).filter((c) => c.requires === 'amazon_ads_api');

  function body() {
    if (checking) return <div className={s.empty}>Checking connection…</div>;
    if (!connected || notConnected) {
      return (
        <div className={s.locked}>
          <div className={s.lockedIcon}><FiLink /></div>
          <h2 className={s.lockedTitle}>Connect Amazon</h2>
          <p className={s.lockedBody}>Attribution reads the reports on your Amazon account.</p>
          <button className={s.button} onClick={() => navigate('/dashboard/market-places')}>
            Connect Amazon
          </button>
        </div>
      );
    }

    return (
      <div className={s.page}>
        {error && <div className={s.error}>{error}</div>}

        <section className={s.panel}>
          <div className={s.contextBar}>
            <div className={s.contextLeft}>
              <label className={s.field}>
                <span className={s.label}>Period</span>
                <select className={s.select} value={period}
                        onChange={(e) => { setPeriod(e.target.value); setStartDate(''); }}>
                  {['WEEK', 'MONTH', 'QUARTER'].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label className={s.field}>
                <span className={s.label}>Window</span>
                <select className={s.select} value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}>
                  {!windowOptions.length && <option value="">No data yet</option>}
                  {windowOptions.map((w) => (
                    <option key={w.start_date} value={w.start_date}>
                      {w.start_date}{w.collected ? '' : ' — not collected'}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {overview?.sources && (
              <div className={s.coverage}>
                Search revenue on {overview.sources.search_revenue} products · totals on{' '}
                {overview.sources.totals} · terms on {overview.sources.terms}. Each figure
                names its own source, so a missing one reads as missing rather than as zero.
              </div>
            )}
          </div>
        </section>

        {!locked && (
          <FeatureCards data={overview} loading={loading && !overview}
                        onOpen={(next) => setTab(next)} />
        )}

        <section className={s.panel}>
          <div className={s.tabs}>
            {TABS.map((t) => (
              <button key={t.id} className={tab === t.id ? s.tabActive : s.tab}
                      onClick={() => setTab(t.id)}>
                {t.label}<span className={s.tabRef}>{t.ref}</span>
              </button>
            ))}
          </div>

          {locked ? (
            <div className={s.locked}>
              <div className={s.lockedIcon}><FiLock /></div>
              <h2 className={s.lockedTitle}>Search data is not available yet</h2>
              <p className={s.lockedBody}>
                Amazon serves these reports to the owner of a brand in Brand Registry. An
                authorised seller of someone else&apos;s brand is refused even when the
                account appears in Brand Registry.
              </p>
            </div>
          ) : (
            <div style={{ paddingTop: 16 }}>
              {tab === 'products' && (
                <>
                  {termParam && (
                    <TermRevenue data={term} loading={termLoading} onClear={clearTerm} />
                  )}
                  <ProductBlocks data={products} loading={loading} />
                </>
              )}
              {tab === 'account' && (
                <AccountView data={account} loading={loading}
                             threshold={account?.dependency_threshold} />
              )}
              {tab === 'long_tail' && <LongTail data={tail} loading={loading} />}
              {tab === 'changed' && (
                <WhatChanged data={changes} styles={s}
                             emptyLine="No product leaned harder on one term, and none lost search share, since the window before." />
              )}
              {tab === 'roi' && (
                <div className={s.blockedPanel}>
                  <div className={s.lockedIcon}><FiLock /></div>
                  <h2 className={s.lockedTitle}>Waiting on the Amazon Ads API</h2>
                  {adsCards.map((c) => (
                    <div key={c.key} className={s.blockedItem}>
                      <h3 className={s.blockedTitle}>
                        <span className={s.cardRef}>{c.ref}</span> {c.title}
                      </h3>
                      <p className={s.lockedBody}>{c.detail}</p>
                    </div>
                  ))}
                  {!adsCards.length && (
                    <p className={s.lockedBody}>
                      Paid spend, paid sales and the organic split need the Amazon Ads API,
                      which is not integrated for this account.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className={shell.featureContainer}>
      <div className={shell.header}>
        <button className={shell.backButton}
                onClick={() => navigate('/dashboard/seller-intelligence')}>
          <FiArrowLeft /> Back
        </button>
        <div className={shell.titleSection}>
          <div className={shell.iconTitle}><h1>Search Attribution</h1></div>
          <p>What search is worth to each product, which terms carry it, and how thin the thread is</p>
        </div>
      </div>
      <div className={shell.content}>{body()}</div>
    </div>
  );
}
